import {
  PurchaseLoadSortOption,
  PurchaseLoadStatus,
  WeightInput,
} from '@rematter/frontend-lib-api-client';
import Big from 'big.js';

import { PurchaseLoadRecord } from '../types/purchaseLoad';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import { parseZonedDateTime } from '~/shared/utils/dateTime';

export const purchaseLoadsListQueryAtom = createQueryAtomFactory(
  'trade.purchaseLoadsList',
  (params: {
    ids?: string[];
    searchTerm?: string;
    facilityId?: string;
    facilityIds?: string[];
    supplierAccountIds?: string[];
    purchaseOrderId?: string;
    purchaseOrderIds?: string[];
    status?: PurchaseLoadStatus[];
    limit?: number;
    offset?: number;
    sorting?: PurchaseLoadSortOption[];
    fromReceivedDate?: string;
    toReceivedDate?: string;
  }) => ({
    purchaseLoads: {
      __args: {
        filter: {
          ids: params.ids,
          searchTerm: params.searchTerm,
          facilityId: params.facilityId,
          facilityIds: params.facilityIds,
          supplierAccountIds: params.supplierAccountIds,
          purchaseOrderId: params.purchaseOrderId,
          purchaseOrderIds: params.purchaseOrderIds,
          status: params.status,
          fromReceivedDate: params.fromReceivedDate,
          toReceivedDate: params.toReceivedDate,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 10,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        status: true,
        documentNumber: true,
        receivedDate: true,
        description: true,
        heavyWeight: {
          value: true,
          unit: true,
        },
        lightWeight: {
          value: true,
          unit: true,
        },
        weightDisplayUnit: true,
        purchaseOrderId: true,
        purchaseOrder: {
          id: true,
          documentNumber: true,
        },
        facilityId: true,
        facility: {
          id: true,
          facilityName: true,
        },
        supplierAccountId: true,
        supplierAccount: {
          id: true,
          name: true,
        },
        serviceId: true,
        service: {
          id: true,
          documentNumber: true,
        },
        supplierAddress: {
          __scalar: true,
        },
        containerNumber: true,
        bolNumber: true,
        internalNotes: true,
        lineItems: {
          id: true,
          netWeight: {
            value: true,
            unit: true,
          },
        },
        files: {
          id: true,
          fileName: true,
          fileSize: true,
          mimeType: true,
          path: true,
        },
        createdByUser: {
          id: true,
          firstName: true,
          lastName: true,
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): {
      purchaseLoads: PurchaseLoadRecord[];
      totalCount: number;
    } => {
      if (!data || !data.purchaseLoads || !data.purchaseLoads.entries) {
        return { purchaseLoads: [], totalCount: 0 };
      }

      if (!data.purchaseLoads.entries.length) {
        return { purchaseLoads: [], totalCount: 0 };
      }

      return {
        purchaseLoads: data.purchaseLoads.entries.map((purchaseLoad) => {
          const lineItems = purchaseLoad.lineItems || [];

          const totalNetWeight = lineItems.reduce<WeightInput | null>(
            (acc, item) => {
              const net = item.netWeight as WeightInput | null;
              if (!net) return acc;
              return {
                value: Big(acc?.value || 0)
                  .plus(net.value)
                  .toNumber(),
                unit: acc?.unit || net.unit,
              };
            },
            null,
          );

          const receivedDate = parseZonedDateTime(purchaseLoad.receivedDate);
          if (!receivedDate) {
            throw new Error('Purchase load missing required receivedDate');
          }

          return {
            id: purchaseLoad.id,
            status: purchaseLoad.status as PurchaseLoadStatus,
            documentNumber: purchaseLoad.documentNumber,
            receivedDate,
            description: purchaseLoad.description || null,
            heavyWeight: purchaseLoad.heavyWeight || null,
            lightWeight: purchaseLoad.lightWeight || null,
            weightDisplayUnit: purchaseLoad.weightDisplayUnit,
            purchaseOrderId: purchaseLoad.purchaseOrderId || null,
            purchaseOrder: purchaseLoad.purchaseOrder || null,
            facilityId: purchaseLoad.facilityId,
            facility: purchaseLoad.facility,
            supplierAccountId: purchaseLoad.supplierAccountId || null,
            supplierAccount: purchaseLoad.supplierAccount || null,
            supplierAddress: purchaseLoad.supplierAddress || null,
            serviceId: purchaseLoad.serviceId ?? null,
            service: purchaseLoad.service ?? null,
            containerTypeId: null,
            carrierAccountId: null,
            freightCost: null,
            freightTerms: null,
            bolNumber: purchaseLoad.bolNumber || null,
            shippingCarrierName: null,
            scac: null,
            driverName: null,
            truckNumber: null,
            trailerNumber: null,
            containerNumber: purchaseLoad.containerNumber || null,
            sealNumber: null,
            internalNotes: purchaseLoad.internalNotes || null,
            externalNotes: null,
            receivingTicketNumber: null,
            receivedTicketIssueDate: null,
            lineItemCount: lineItems.length,
            totalNetWeight,
            supplierName: purchaseLoad.supplierAccount?.name || undefined,
            files: purchaseLoad.files?.map((f) => ({
              id: f.id,
              fileName: f.fileName,
              fileSize: f.fileSize,
              mimeType: f.mimeType,
              path: f.path,
            })),
            createdByUser: purchaseLoad.createdByUser
              ? {
                  id: purchaseLoad.createdByUser.id,
                  firstName: purchaseLoad.createdByUser.firstName,
                  lastName: purchaseLoad.createdByUser.lastName,
                }
              : null,
            timestamps: {
              createdAt: parseZonedDateTime(purchaseLoad.timestamps.createdAt),
              updatedAt: parseZonedDateTime(purchaseLoad.timestamps.updatedAt),
            },
          } as PurchaseLoadRecord;
        }),
        totalCount: data.purchaseLoads.page?.total || 0,
      };
    },
  },
);
